/**
 * When an account has more than one address, every billing row gets a coloured
 * tag for the address it was found under — so "which of his two emails is this
 * subscription on?" is answered by looking, not by reading.
 *
 * The colour comes from the address's position in `emailsQueried`, which the
 * API returns current-first, so the same address keeps the same colour across
 * the header, the subscriptions table and the payment timeline.
 *
 * As with the plan badge, the colour is carried by the dot and the tint, never
 * by the text: a coloured label has to hold contrast on every background and in
 * both themes, and `text-foreground` does that by construction.
 */
import { cn } from "@/lib/utils";

const TONES = ["#0ea5e9", "#8b5cf6", "#10b981", "#f43f5e", "#f59e0b"];

function tone(email: string, all: string[]): string {
  const i = all.findIndex((e) => e.toLowerCase() === email.toLowerCase());
  return TONES[(i < 0 ? all.length : i) % TONES.length];
}

export function EmailTag({
  email,
  all,
  current,
  className,
}: {
  email: string;
  all: string[];
  /** Marks the account's present address, so the others read as history. */
  current?: boolean;
  className?: string;
}) {
  const color = tone(email, all);
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 truncate rounded-full border px-2 py-0.5 text-xs font-medium text-foreground",
        className
      )}
      style={{ backgroundColor: `${color}22`, borderColor: `${color}80` }}
      title={current ? `${email} (current address)` : email}
    >
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="truncate">{email}</span>
      {current && <span className="shrink-0 opacity-60">· current</span>}
    </span>
  );
}
